import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Loading from '../../../../components/Loading';
import {
   getJobOpening,
   createJobOpening,
   updateJobOpening,
} from '../../../../services/hr/recruitment/jobOpeningsApi';
import { Container } from '../../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../../styles/layout/Section';
import {
   ActionBar,
   ActionBarLeft,
   ActionBarRight,
} from '../../../../styles/layout/ActionBar';
import { FormGrid } from '../../../../styles/layout/FormGrid';
import { Field, RequiredMark } from '../../../../styles/forms/Field';
import { GhostButton, PrimaryButton } from '../../../../styles/ui/Button';

import JobPositionSelect from '../components/JobPositionSelect';
import { JOB_OPENING_STATUS_LABEL } from '../constants';
import { readApiError, recruitmentErrorMessage } from '../utils/apiError';

// ---------------------------------------------------------------------------
// RH · Recrutamento — cadastro/edição de vaga (handoff 0061 §4.2). Cria ABERTA
// (opened_at = hoje). `status` só aparece na edição (mudar p/ ENCERRADA/
// PREENCHIDA seta closed_at no backend). Opcionais vazios são OMITIDOS (o Zod
// rejeita null). Erro: 422 com field → inline; resto → toast.
// ---------------------------------------------------------------------------

const FIELD_ERROR_STYLE = {
   fontSize: 11,
   color: '#b71c1c',
   marginTop: 2,
};

const STATUS_OPTIONS = Object.keys(JOB_OPENING_STATUS_LABEL);

const EMPTY_FORM = {
   job_position_id: '',
   title: '',
   openings_count: '1',
   notes: '',
   status: '',
};

export default function OpeningForm() {
   const { id } = useParams();
   const navigate = useNavigate();
   const isEditMode = Boolean(id);

   const [form, setForm] = useState(EMPTY_FORM);
   const [fieldErrors, setFieldErrors] = useState({});
   const [isLoading, setIsLoading] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);

   const fetchOpeningForEdit = useCallback(async () => {
      setIsLoading(true);
      try {
         const opening = await getJobOpening(id);
         if (!opening) {
            toast.error('Vaga não encontrada.');
            navigate('/hr/recruitment/openings');
            return;
         }
         setForm({
            job_position_id: opening.job_position_id
               ? String(opening.job_position_id)
               : '',
            title: opening.title ?? '',
            openings_count:
               opening.openings_count != null
                  ? String(opening.openings_count)
                  : '1',
            notes: opening.notes ?? '',
            status: opening.status ?? '',
         });
      } catch (err) {
         toast.error(recruitmentErrorMessage(err, 'Erro ao carregar a vaga.'));
         navigate('/hr/recruitment/openings');
      } finally {
         setIsLoading(false);
      }
   }, [id, navigate]);

   useEffect(() => {
      if (isEditMode) fetchOpeningForEdit();
   }, [isEditMode, fetchOpeningForEdit]);

   const updateField = (key, value) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      setFieldErrors((prev) => {
         if (!prev[key]) return prev;
         const next = { ...prev };
         delete next[key];
         return next;
      });
   };

   function buildPayload() {
      // Opcionais vazios são OMITIDOS (o Zod rejeita null). status só na edição.
      const payload = { job_position_id: Number(form.job_position_id) };
      if (form.title.trim()) payload.title = form.title.trim();
      if (form.openings_count) {
         payload.openings_count = Number(form.openings_count);
      }
      if (form.notes.trim()) payload.notes = form.notes.trim();
      if (isEditMode && form.status) payload.status = form.status;
      return payload;
   }

   function handleSubmitError(err) {
      const { status, fieldErrors: apiFieldErrors } = readApiError(err);
      if (status === 409) {
         toast.error(recruitmentErrorMessage(err, 'Registro em conflito.'));
         return;
      }
      if (Object.keys(apiFieldErrors).length > 0) {
         setFieldErrors(apiFieldErrors);
         toast.error(
            err?.response?.data?.message ?? 'Verifique os campos destacados.',
         );
         return;
      }
      toast.error(recruitmentErrorMessage(err, 'Erro ao salvar a vaga.'));
   }

   async function handleSubmit(event) {
      event.preventDefault();
      if (isSubmitting) return;

      const clientErrors = {};
      if (!form.job_position_id) {
         clientErrors.job_position_id = 'Selecione o cargo da vaga.';
      }
      if (form.openings_count && Number(form.openings_count) < 1) {
         clientErrors.openings_count = 'Informe pelo menos 1 vaga.';
      }
      if (Object.keys(clientErrors).length > 0) {
         setFieldErrors(clientErrors);
         toast.error('Verifique os campos destacados.');
         return;
      }

      setIsSubmitting(true);
      setFieldErrors({});
      try {
         const payload = buildPayload();
         if (isEditMode) {
            const { jobOpening, message } = await updateJobOpening(id, payload);
            toast.success(message);
            navigate(`/hr/recruitment/openings/${jobOpening?.id ?? id}`);
         } else {
            const { jobOpening, message } = await createJobOpening(payload);
            toast.success(message);
            navigate(`/hr/recruitment/openings/${jobOpening?.id}`);
         }
      } catch (err) {
         handleSubmitError(err);
      } finally {
         setIsSubmitting(false);
      }
   }

   const backTo = isEditMode
      ? `/hr/recruitment/openings/${id}`
      : '/hr/recruitment/openings';

   return (
      <Container>
         <Loading isLoading={isLoading || isSubmitting} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>{isEditMode ? 'Editar vaga' : 'Nova vaga'}</PageTitle>
               <PageSubtitle>
                  {isEditMode
                     ? 'Atualize os dados e o status da vaga.'
                     : 'Abra uma nova vaga vinculada a um cargo.'}
               </PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               <GhostButton
                  type="button"
                  onClick={() => navigate(backTo)}
                  disabled={isSubmitting}
               >
                  Cancelar
               </GhostButton>
            </PageHeaderActions>
         </PageHeader>

         <form onSubmit={handleSubmit}>
            <Section>
               <SectionHeader>
                  <SectionTitle>Dados da vaga</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  <FormGrid>
                     <Field className="col-6">
                        <label htmlFor="opening-position">
                           Cargo
                           <RequiredMark>*</RequiredMark>
                        </label>
                        <JobPositionSelect
                           id="opening-position"
                           value={form.job_position_id}
                           onChange={(value) =>
                              updateField('job_position_id', value)
                           }
                           disabled={isSubmitting}
                           invalid={!!fieldErrors.job_position_id}
                        />
                        {fieldErrors.job_position_id && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.job_position_id}
                           </span>
                        )}
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="opening-title">
                           Título (opcional)
                        </label>
                        <input
                           id="opening-title"
                           type="text"
                           maxLength={150}
                           placeholder="Ex: Técnico de campo — SP"
                           value={form.title}
                           onChange={(e) => updateField('title', e.target.value)}
                           disabled={isSubmitting}
                           aria-invalid={!!fieldErrors.title || undefined}
                        />
                        {fieldErrors.title && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.title}
                           </span>
                        )}
                     </Field>

                     <Field className="col-2">
                        <label htmlFor="opening-count">Nº de vagas</label>
                        <input
                           id="opening-count"
                           type="number"
                           min={1}
                           value={form.openings_count}
                           onChange={(e) =>
                              updateField('openings_count', e.target.value)
                           }
                           disabled={isSubmitting}
                           aria-invalid={!!fieldErrors.openings_count || undefined}
                        />
                        {fieldErrors.openings_count && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.openings_count}
                           </span>
                        )}
                     </Field>

                     {isEditMode && (
                        <Field className="col-4">
                           <label htmlFor="opening-status">Status</label>
                           <select
                              id="opening-status"
                              value={form.status}
                              onChange={(e) =>
                                 updateField('status', e.target.value)
                              }
                              disabled={isSubmitting}
                           >
                              {STATUS_OPTIONS.map((status) => (
                                 <option key={status} value={status}>
                                    {JOB_OPENING_STATUS_LABEL[status]}
                                 </option>
                              ))}
                           </select>
                        </Field>
                     )}

                     <Field className="col-12">
                        <label htmlFor="opening-notes">Observações</label>
                        <textarea
                           id="opening-notes"
                           rows={3}
                           value={form.notes}
                           onChange={(e) => updateField('notes', e.target.value)}
                           disabled={isSubmitting}
                           placeholder="Detalhes internos da vaga..."
                           aria-invalid={!!fieldErrors.notes || undefined}
                        />
                        {fieldErrors.notes && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.notes}
                           </span>
                        )}
                     </Field>
                  </FormGrid>
               </SectionBody>
            </Section>

            <ActionBar>
               <ActionBarLeft />
               <ActionBarRight>
                  <GhostButton
                     type="button"
                     onClick={() => navigate(backTo)}
                     disabled={isSubmitting}
                  >
                     Cancelar
                  </GhostButton>
                  <PrimaryButton type="submit" disabled={isSubmitting}>
                     {isSubmitting
                        ? 'Salvando...'
                        : isEditMode
                          ? 'Salvar alterações'
                          : 'Criar vaga'}
                  </PrimaryButton>
               </ActionBarRight>
            </ActionBar>
         </form>
      </Container>
   );
}
